import { LatLon } from '../domain/types.ts';

export interface LocationResult {
  coords: LatLon;
  label: string;
  source?: 'gps' | 'ip' | 'default' | 'search';
  isEstimated?: boolean;
}

export class LocationService {
  private static DEFAULT_LOCATION: LocationResult = {
    coords: { lat: 21.0135, lon: 105.8504 },
    label: 'Phố Huế, Hai Bà Trưng, Hà Nội (Mặc định)',
    source: 'default',
    isEstimated: true,
  };

  /**
   * Lấy vị trí thực tế của người chơi:
   * 1. Thử lấy GPS/WiFi triangulation từ thiết bị (độ chính xác con phố/mét)
   * 2. Nếu thiết bị không cấp quyền hoặc lỗi, lấy ước tính từ IP và ĐÁNH DẤU RÕ RÀNG là ước tính
   */
  public static async getCurrentLocation(timeoutMs = 8000): Promise<LocationResult> {
    // 1. Thử lấy định vị thiết bị (WiFi BSSID / GPS vệ tinh)
    const deviceResult = await this.tryDeviceGPS(timeoutMs);
    if (deviceResult) {
      const resolvedLabel = await this.reverseGeocode(deviceResult.coords.lat, deviceResult.coords.lon);
      return {
        coords: deviceResult.coords,
        label: resolvedLabel || deviceResult.label,
        source: 'gps',
        isEstimated: false,
      };
    }

    // 2. Nếu thiết bị chặn quyền hoặc không có GPS, thử IP nhưng đánh dấu là ước tính (không đánh lừa người chơi)
    const ipResult = await this.tryIpGeolocation();
    if (ipResult) {
      const resolvedLabel = await this.reverseGeocode(ipResult.coords.lat, ipResult.coords.lon);
      return {
        coords: ipResult.coords,
        label: resolvedLabel || ipResult.label,
        source: 'ip',
        isEstimated: true,
      };
    }

    // 3. Fallback cuối cùng nếu offline hoàn toàn
    return this.DEFAULT_LOCATION;
  }

  /**
   * Truy vấn vị trí qua IP mạng (độ chính xác cấp tỉnh/thành phố, mang tính ước tính)
   */
  private static async tryIpGeolocation(): Promise<LocationResult | null> {
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 4000);

      const res = await fetch('https://ipwho.is/', {
        signal: controller.signal,
      });
      window.clearTimeout(timeoutId);

      if (!res.ok) return null;
      const data = await res.json() as {
        success?: boolean;
        city?: string;
        region?: string;
        latitude?: number;
        longitude?: number;
      };

      if (data && data.success !== false && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
        const city = data.city || data.region || 'Thành phố của bạn';
        return {
          coords: { lat: data.latitude, lon: data.longitude },
          label: `${city} (Ước tính từ IP mạng)`,
          source: 'ip',
          isEstimated: true,
        };
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Lấy tọa độ từ thiết bị với 2 chế độ:
   * - Phase 1: enableHighAccuracy: false (WiFi/Cell triangulation, hoạt động cực tốt trên laptop/PC và smartphone không cần chip GPS ngoài trời)
   * - Phase 2: enableHighAccuracy: true (nếu Phase 1 không có kết quả)
   */
  private static async tryDeviceGPS(timeoutMs: number): Promise<LocationResult | null> {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      return null;
    }

    // Phase 1: WiFi/Cell triangulation (Nhanh và tin cậy trên máy tính lẫn điện thoại)
    const quickResult = await this.queryBrowserPosition({
      enableHighAccuracy: false,
      timeout: Math.min(5000, timeoutMs),
      maximumAge: 120000,
    });
    if (quickResult) return quickResult;

    // Phase 2: Hardware GPS vệ tinh (dành cho điện thoại ngoài trời)
    const highAccResult = await this.queryBrowserPosition({
      enableHighAccuracy: true,
      timeout: Math.min(5000, timeoutMs),
      maximumAge: 60000,
    });
    return highAccResult;
  }

  private static queryBrowserPosition(options: PositionOptions): Promise<LocationResult | null> {
    return new Promise((resolve) => {
      let isSettled = false;
      const timer = window.setTimeout(() => {
        if (!isSettled) {
          isSettled = true;
          resolve(null);
        }
      }, options.timeout || 5000);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (!isSettled) {
            isSettled = true;
            window.clearTimeout(timer);
            resolve({
              coords: {
                lat: position.coords.latitude,
                lon: position.coords.longitude,
              },
              label: 'Vị trí thiết bị của bạn',
              source: 'gps',
              isEstimated: false,
            });
          }
        },
        () => {
          if (!isSettled) {
            isSettled = true;
            window.clearTimeout(timer);
            resolve(null);
          }
        },
        options
      );
    });
  }

  /**
   * Tra cứu tên phố / quận / thành phố từ tọa độ (Reverse Geocode)
   */
  public static async reverseGeocode(lat: number, lon: number): Promise<string | null> {
    try {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 4000);

      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
        {
          signal: controller.signal,
          headers: {
            'User-Agent': 'BaChuViaHe/2.0 (game; contact: dev@bachuviahe.com)',
            'Accept-Language': 'vi,en;q=0.9',
          },
        }
      );
      window.clearTimeout(timeoutId);

      if (!res.ok) return null;
      const data = await res.json() as { display_name?: string };

      if (data && typeof data.display_name === 'string') {
        const parts = data.display_name.split(',').map((s) => s.trim());
        // Lấy 3 thành phần đầu để có tên phố + phường/quận + thành phố gọn gàng
        return parts.slice(0, 3).join(', ');
      }
      return null;
    } catch {
      return null;
    }
  }
}
